/**
 * Sentry Breadcrumbs Utilities
 * 
 * Helper functions for tracking user actions as Sentry breadcrumbs.
 * These help understand the sequence of events leading to errors.
 */

import * as Sentry from "@sentry/react";

/**
 * Track navigation between pages/routes
 */
export function trackNavigation(path: string, metadata?: Record<string, any>): void {
  Sentry.addBreadcrumb({
    category: "navigation",
    level: "info",
    message: `Navigated to ${path}`,
    data: {
      path,
      ...metadata,
    },
  });
}

/**
 * Track form submissions
 */
export function trackFormSubmit(
  formName: string,
  data?: Record<string, any>
): void {
  // Don't log sensitive form data, just field names
  const sanitizedData: Record<string, string> = {};
  if (data) {
    Object.keys(data).forEach((key) => {
      sanitizedData[key] = typeof data[key];
    });
  }

  Sentry.addBreadcrumb({
    category: "form",
    level: "info",
    message: `Form submitted: ${formName}`,
    data: {
      formName,
      fields: sanitizedData,
    },
  });
}

/**
 * Track authentication state changes
 */
export function trackAuthStateChange(
  state: "login" | "login_failed" | "logout" | "auth_check_success" | "auth_check_failed",
  metadata?: Record<string, any>
): void {
  const levelMap: Record<string, Sentry.SeverityLevel> = {
    login: "info",
    login_failed: "warning",
    logout: "info",
    auth_check_success: "info",
    auth_check_failed: "warning",
  };

  Sentry.addBreadcrumb({
    category: "auth",
    level: levelMap[state] || "info",
    message: `Authentication: ${state}`,
    data: {
      state,
      ...metadata,
    },
  });
}

/**
 * Track critical user actions (property creation, payment, etc.)
 */
export function trackCriticalAction(
  action: string,
  metadata?: Record<string, any>
): void {
  Sentry.addBreadcrumb({
    category: "user-action",
    level: "warning", // Higher visibility for critical actions
    message: `Critical action: ${action}`,
    data: {
      action,
      timestamp: new Date().toISOString(),
      ...metadata,
    },
  });
}

/**
 * Track property viewing/editing
 */
export function trackPropertyAction(
  action: "view" | "edit" | "create" | "delete",
  propertyId?: string,
  metadata?: Record<string, any>
): void {
  Sentry.addBreadcrumb({
    category: "property",
    level: "info",
    message: `Property ${action}${propertyId ? `: ${propertyId}` : ""}`,
    data: {
      action,
      propertyId,
      ...metadata,
    },
  });
}

/**
 * Track subscription/billing actions
 */
export function trackBillingAction(
  action: "view" | "update" | "cancel" | "upgrade" | "downgrade" | "give_discount" | "cancel_discount" | "cancel_subscription",
  metadata?: Record<string, any>
): void {
  Sentry.addBreadcrumb({
    category: "billing",
    level: "warning", // Higher visibility for billing actions
    message: `Billing action: ${action}`,
    data: {
      action,
      timestamp: new Date().toISOString(),
      ...metadata,
    },
  });
}

/**
 * Track API errors (called from api utilities)
 */
export function trackApiError(
  endpoint: string,
  status: number,
  metadata?: Record<string, any>
): void {
  Sentry.addBreadcrumb({
    category: "api",
    level: status >= 500 ? "error" : "warning",
    message: `API error: ${endpoint}`,
    data: {
      endpoint,
      status,
      ...metadata,
    },
  });
}

/**
 * Track data mutations (create, update, delete)
 */
export function trackDataMutation(
  action: "create" | "update" | "delete",
  resource: string,
  resourceId?: string,
  metadata?: Record<string, any>
): void {
  Sentry.addBreadcrumb({
    category: "data",
    level: "info",
    message: `Data ${action}: ${resource}${resourceId ? ` (${resourceId})` : ""}`,
    data: {
      action,
      resource,
      resourceId,
      ...metadata,
    },
  });
}
